import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ProviderRoster } from "@/components/admin/ProviderRoster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Inbox, UserCheck, UserCog, Briefcase, Wallet, ArrowRight, CheckCircle2, AlertTriangle, Zap,
  ChevronRight, Search, X, SearchX,
} from "lucide-react";

const MISSING_LABEL = {
  account_incomplete: "Account incomplete",
  documents_not_all_approved: "Documents not all approved",
  provider_agreement_not_accepted: "ARPS Provider Service Agreement not accepted",
  connect_payouts_not_enabled: "Stripe Connect not enabled",
};

function fmtMoney(amount, currency) {
  if (amount === undefined || amount === null) return "—";
  return `${currency || "USD"} ${Number(amount).toFixed(2)}`;
}

function Section({ icon: Icon, title, count, shown, moreHref, moreLabel, tone = "slate", testid, children }) {
  const nav = useNavigate();
  const dot = { slate: "bg-slate-400", sky: "bg-sky-500", amber: "bg-amber-500", emerald: "bg-emerald-500", violet: "bg-violet-500", rose: "bg-rose-500" }[tone];
  const truncated = shown != null && count > shown;
  return (
    <section data-testid={testid} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <header className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${dot} bg-opacity-15`}>
          <Icon className="h-4 w-4 text-slate-700" />
        </span>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <span data-testid={`${testid}-count`} className={`ml-auto rounded-full ${dot} px-2 py-0.5 text-xs font-bold text-white`}>{count}</span>
      </header>
      <div className="divide-y divide-slate-100">{children}</div>
      {truncated && (
        <button data-testid={`${testid}-more`} onClick={() => moreHref && nav(moreHref)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 text-xs font-medium text-slate-500 hover:text-slate-800">
          Showing {shown} of {count} — {moreLabel || "open full list"}<ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </section>
  );
}

function Row({ testid, title, subtitle, meta, chips, actions, onOpen }) {
  return (
    <div data-testid={testid} onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpen) { e.preventDefault(); onOpen(); } }}
      className="group flex cursor-pointer flex-col gap-3 px-4 py-3 transition-colors hover:bg-slate-50 focus:bg-slate-50 focus:outline-none sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-900 group-hover:text-slate-950">{title}</div>
        {subtitle && <div className="truncate text-xs text-slate-500">{subtitle}</div>}
        {chips && chips.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                <AlertTriangle className="h-3 w-3" />{c}
              </span>
            ))}
          </div>
        )}
      </div>
      {meta && <div className="text-xs text-slate-500 sm:text-right">{meta}</div>}
      {actions && <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>{actions}</div>}
      <ChevronRight className="hidden h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500 sm:block" />
    </div>
  );
}

export default function AdminIntake() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("action");
  const [rosterBadge, setRosterBadge] = useState(0);

  useEffect(() => {
    api.get("/admin/provider-roster", { params: { status: "onboarding_incomplete" } })
      .then(({ data }) => setRosterBadge(data.status_counts?.onboarding_incomplete || 0))
      .catch(() => {});
  }, [view]);

  const load = useCallback(async (query) => {
    try {
      const { data } = await api.get("/admin/intake-queue", { params: query ? { q: query } : {} });
      setData(data);
    } catch {
      toast.error("Could not load the intake queue");
    }
  }, []);

  // Debounced server-side search: filters the actionable rows without touching the badge.
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const refreshBadge = () => window.dispatchEvent(new Event("intake-refresh"));

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load(q.trim());
      refreshBadge();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const approveClient = (c) => act(`client-${c.id}`, () => api.post(`/admin/clients/${c.id}/activate`), "Client approved");
  const approveServer = (s) => act(`server-${s.id}`, () => api.post(`/servers/${s.id}/approve`, { notes: "" }), "Server approved");
  const markPayout = (p) => act(`payout-${p.id}`, () => api.post(`/compensations/${p.id}/pay`, {}), "Payout marked paid");

  const counts = data?.counts || {};          // actionable totals (badge / summary) — never change with search
  const fc = data?.filtered_counts || counts; // section counts while searching
  const searching = q.trim().length > 0;

  const summary = [
    { key: "clients", label: "Client approvals", value: counts.clients, tone: "sky" },
    { key: "servers", label: "Server approvals", value: counts.servers, tone: "violet" },
    { key: "assignments", label: "Assignments", value: counts.assignments, tone: "amber" },
    { key: "payouts", label: "Payout requests", value: counts.payouts, tone: "emerald" },
    { key: "exceptions", label: "Exceptions", value: counts.exceptions, tone: "rose" },
  ].filter((s) => s.key !== "exceptions" || (s.value || 0) > 0);
  const toneText = { sky: "text-sky-600", violet: "text-violet-600", amber: "text-amber-600", emerald: "text-emerald-600", rose: "text-rose-600" };

  return (
    <div data-testid="admin-intake" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Intake</h1>
        <p className="text-sm text-slate-500">Action Center for items needing your attention, and the full Provider roster.</p>
      </div>

      <div className="flex gap-2" data-testid="intake-view-toggle">
        <button data-testid="intake-view-action" onClick={() => setView("action")}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${view === "action" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Action Center
        </button>
        <button data-testid="intake-view-roster" onClick={() => setView("roster")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${view === "roster" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Provider Roster
          {rosterBadge > 0 && <span data-testid="intake-roster-badge" className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{rosterBadge}</span>}
        </button>
      </div>

      {view === "roster" && <ProviderRoster />}
      {view === "action" && (
      <div className="space-y-6">

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="intake-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search Intake..." className="h-11 rounded-xl pl-10 pr-10" />
        {q && (
          <button data-testid="intake-search-clear" onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {searching ? (
        <div data-testid="intake-search-results" className="text-sm font-medium text-slate-600">
          {data?.filtered_total ?? 0} matching {(data?.filtered_total ?? 0) === 1 ? "result" : "results"} for “{q.trim()}”
        </div>
      ) : (
        <div data-testid="intake-summary" className={`grid grid-cols-2 gap-3 ${summary.length >= 5 ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
          {summary.map((s) => (
            <div key={s.key} data-testid={`summary-${s.key}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
              <div className={`text-2xl font-bold ${(s.value || 0) > 0 ? toneText[s.tone] : "text-slate-300"}`}>{s.value ?? 0}</div>
              <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {data && !searching && (data.total || 0) === 0 && (
        <div data-testid="intake-empty" className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/50 p-12 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <p className="font-semibold text-slate-800">You're all caught up</p>
          <p className="text-sm text-slate-500">No client or server approvals, assignments, or payout requests need your attention.</p>
        </div>
      )}

      {data && searching && (data.filtered_total || 0) === 0 && (
        <div data-testid="intake-no-results" className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <SearchX className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-semibold text-slate-700">No matching items</p>
          <p className="text-sm text-slate-500">Nothing in the actionable queue matches “{q.trim()}”.</p>
        </div>
      )}

      {(fc.clients || 0) > 0 && (
        <Section icon={UserCheck} title="Client approvals" count={fc.clients} shown={data.clients.length} moreHref="/admin/clients" moreLabel="open Clients" tone="sky" testid="section-clients">
          {data.clients.map((c) => (
            <Row key={c.id} testid={`client-row-${c.id}`} onOpen={() => nav(`/admin/clients/${c.id}`)}
              title={c.name || c.email} subtitle={[c.company, c.email].filter(Boolean).join(" · ")}
              meta={c.submitted ? new Date(c.submitted).toLocaleDateString() : null}
              chips={[c.status].filter(Boolean)}
              actions={<Button size="sm" data-testid={`client-approve-${c.id}`} disabled={busy === `client-${c.id}`} onClick={() => approveClient(c)} className="bg-slate-900 text-white hover:bg-slate-800"><UserCheck className="mr-1.5 h-4 w-4" />Approve</Button>} />
          ))}
        </Section>
      )}

      {(fc.servers || 0) > 0 && (
        <Section icon={UserCog} title="Provider approvals" count={fc.servers} shown={data.servers.length} moreHref="/admin/servers" moreLabel="open Providers" tone="violet" testid="section-servers">
          {data.servers.map((s) => {
            const chips = (s.missing || []).map((m) => MISSING_LABEL[m] || m);
            return (
              <Row key={s.id} testid={`server-row-${s.id}`} onOpen={() => nav(`/admin/servers/${s.id}`)}
                title={s.name || s.email} subtitle={[s.email, s.service_area].filter(Boolean).join(" · ")}
                meta={s.docs_required != null ? `Docs ${s.docs_approved || 0}/${s.docs_required}` : null}
                chips={chips}
                actions={<Button size="sm" data-testid={`server-approve-${s.id}`} disabled={!s.can_approve || busy === `server-${s.id}`} onClick={() => approveServer(s)} className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"><UserCheck className="mr-1.5 h-4 w-4" />Approve</Button>} />
            );
          })}
        </Section>
      )}

      {(fc.assignments || 0) > 0 && (
        <Section icon={Briefcase} title="Assignments requiring action" count={fc.assignments} shown={data.assignments.length} moreHref="/admin/jobs" moreLabel="open All Jobs" tone="amber" testid="section-assignments">
          {data.assignments.map((o) => (
            <Row key={o.id} testid={`assignment-row-${o.id}`} onOpen={() => nav(`/admin/jobs/${o.id}`)}
              title={`#${o.order_number || o.id}`} subtitle={[o.client, o.defendant].filter(Boolean).join(" → ") || o.service_area}
              meta={o.deadline ? `Due ${new Date(o.deadline).toLocaleDateString()}` : null}
              chips={[o.reason]}
              actions={<Button size="sm" data-testid={`assignment-assign-${o.id}`} onClick={() => nav(`/admin/jobs/${o.id}`)} className="bg-slate-900 text-white hover:bg-slate-800">Assign<ArrowRight className="ml-1.5 h-4 w-4" /></Button>} />
          ))}
        </Section>
      )}

      {(fc.payouts || 0) > 0 && (
        <Section icon={Wallet} title="Payout requests — awaiting approval" count={fc.payouts} shown={data.payouts.length} moreHref="/admin/payouts" moreLabel="open Payouts" tone="emerald" testid="section-payouts">
          {data.payouts.map((p) => (
            <Row key={p.id} testid={`payout-row-${p.id}`} onOpen={() => nav(p.order_id ? `/admin/jobs/${p.order_id}` : "/admin/payouts")}
              title={`${fmtMoney(p.amount, p.currency)} — ${p.server || "server"}`}
              subtitle={`Job #${p.order_number || ""}${p.method ? ` · ${p.method}` : ""} · ${p.status}`}
              meta={p.completed ? new Date(p.completed).toLocaleDateString() : null}
              actions={<Button size="sm" data-testid={`payout-pay-${p.id}`} disabled={busy === `payout-${p.id}`} onClick={() => markPayout(p)} className="bg-emerald-600 text-white hover:bg-emerald-700">Approve Payout</Button>} />
          ))}
        </Section>
      )}

      {(fc.exceptions || 0) > 0 && data.exceptions && (
        <Section icon={Zap} title="Urgent / exceptions" count={fc.exceptions} tone="rose" testid="section-exceptions">
          {data.exceptions.map((x) => (
            <Row key={x.id} testid={`exception-row-${x.id}`} onOpen={() => x.href && nav(x.href)}
              title={x.title || x.id} subtitle={x.subtitle}
              chips={x.reason ? [x.reason] : null} />
          ))}
        </Section>
      )}

      {!data && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
          <Inbox className="mx-auto mb-3 h-8 w-8 text-slate-300" />Loading intake queue…
        </div>
      )}
    </div>
      )}
    </div>
  );
}
